import { Call, Device } from '@twilio/voice-sdk';
import { TwilioCall } from '@/types/app.types';
import { deviceOptions } from '@modules/phone/constants/device.constants';
import { ActiveCall, ActiveDevice, CallDirection, CallProvider, CallStatus, OutgoingCallParams } from '../types';
import { registerProvider } from '../provider-registry';
import { isMemberCall } from './helpers';

function asTwilioCall(call: ActiveCall) {
  return call.providerCall as TwilioCall;
}

function asTwilioDevice(device: ActiveDevice) {
  return device.providerDevice as Device;
}

function toTwilioConnectParams(params: OutgoingCallParams): Record<string, string> {
  if (params.kind === 'number') return { From: params.from, To: params.to };
  return { strategyExternalId: params.strategyExternalId, strategyReferenceId: params.strategyReferenceId };
}

export const twilioProvider: CallProvider = {
  createDevice: (token) => {
    const device = new Device(token, deviceOptions);
    device.register().catch((error) => console.error('Twilio device registration failed', error));
    return { provider: 'twilio', providerDevice: device };
  },
  updateDeviceToken: (device, token) => asTwilioDevice(device).updateToken(token),
  getDeviceToken: (device) => asTwilioDevice(device).token ?? '',
  isDeviceBusy: (device) => asTwilioDevice(device).isBusy,
  getActiveCallCount: (device) => asTwilioDevice(device).calls.length,
  disconnectAllCalls: (device) => asTwilioDevice(device).disconnectAll(),
  connect: async (device, params) => {
    const call = await asTwilioDevice(device).connect({ params: toTwilioConnectParams(params) });
    return { provider: 'twilio', providerCall: call };
  },

  accept: (call) => asTwilioCall(call).accept(),
  reject: (call) => asTwilioCall(call).reject(),
  ignore: (call) => asTwilioCall(call).ignore(),
  disconnect: (call) => asTwilioCall(call).disconnect(),
  mute: (call, shouldMute) => asTwilioCall(call).mute(shouldMute),
  sendDigits: (call, digits) => asTwilioCall(call).sendDigits(digits),
  getCallId: (call) => asTwilioCall(call).parameters.CallSid,
  getDirection: (call): CallDirection =>
    asTwilioCall(call).direction === Call.CallDirection.Incoming ? 'incoming' : 'outgoing',
  getStatus: (call) => asTwilioCall(call).status() as CallStatus,
  isAnswered: (call) => !!asTwilioCall(call)._isAnswered,
  isMemberCall: (call) => isMemberCall(asTwilioCall(call)),
  getCustomParam: (call, key) => asTwilioCall(call).customParameters.get(key),
};

registerProvider('twilio', twilioProvider);
